import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { FlexBox } from 'components/Box'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getTradingAccountFilterFields } from 'components/Filter/tradingAccountsFields'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridState } from 'features/DataGrid'
import uniqBy from 'lodash/uniqBy'
import { useAppDispatch } from 'store'
import {
  fetchCountriesAction,
  fetchFilterAction,
  fetchFilterTypesAction,
  fetchTickmillCompaniesAction,
} from 'store/identity/identity.actions'
import {
  fetchBooksTypeAction,
  fetchPlatformTypesAction,
  fetchTradingAccountGroupTypeAction,
  fetchTradingAccountsLeverageTypesAction,
  fetchTradingAccountStatusAction,
  fetchTradingAccountTypeAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, instanceOfFilterDescriptor } from 'utils/QueryHelpers'

import { normalizeTradingAccountsSearchQuery } from './TradingAccounts/normalizer'

import './TradingAccount.scss'

import { FilterDescriptor, GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  return {
    fetchTradingAccountsLeverageType: () =>
      dispatch(fetchTradingAccountsLeverageTypesAction(tradingAccountsApiClient)),
    fetchPlatformTypes: () => dispatch(fetchPlatformTypesAction(tradingAccountsApiClient)),
    fetchBookTypes: () => dispatch(fetchBooksTypeAction(tradingAccountsApiClient)),
    fetchTradingAccountStatus: () =>
      dispatch(fetchTradingAccountStatusAction(tradingAccountsApiClient)),
    fetchCountries: () => dispatch(fetchCountriesAction(identityApiClient)),
    fetchTradingAccountType: () =>
      dispatch(fetchTradingAccountTypeAction(tradingAccountsApiClient)),
    fetchTradingAccountGroupType: () =>
      dispatch(fetchTradingAccountGroupTypeAction(tradingAccountsApiClient)),
    fetchTickmillCompanies: () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchFilterTypes: () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: () => dispatch(fetchFilterAction(identityApiClient)),
  }
}

export type SelectTradingAccountEvent = { filterId?: string; value: TradingAccountDto[] }

interface SelectTradingAccountTableProps {
  filterId?: string
  onSelect: (tradingAccounts: SelectTradingAccountEvent) => void
  onClose: () => void
}

const SelectTradingAccountTable: React.FC<SelectTradingAccountTableProps> = (props) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const { gridState } = useGridState()
  const [isLoading, setLoading] = useState<boolean>(true)
  const [tradingAccounts, setTradingAccounts] = useState<TradingAccountDto[]>([])
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )
  const [selectedTradingAccounts, setSelectedTradingAccounts] = useState<TradingAccountDto[]>([])
  const {
    fetchTradingAccountsLeverageType,
    fetchPlatformTypes,
    fetchBookTypes,
    fetchTradingAccountStatus,
    fetchCountries,
    fetchTradingAccountType,
    fetchTradingAccountGroupType,
    fetchTickmillCompanies,
  } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterActionState: GridFilterActionState
        ): Promise<PageData<TradingAccountDto[]>> => {
          const response = await tradingAccountsApiClient.getTradingAccounts({
            pageIndex: gridFilterActionState.pageIndex,
            pageSize: gridFilterActionState.pageSize,
          })

          await fetchTradingAccountsLeverageType()
          await fetchPlatformTypes()
          await fetchBookTypes()
          await fetchTradingAccountStatus()
          await fetchCountries()
          await fetchTradingAccountType()
          await fetchTradingAccountGroupType()
          await fetchTickmillCompanies()

          const tradingAccountsFilter = findFilterById(props.filterId, gridState.filter.filters)
          const value =
            tradingAccountsFilter?.value?.filter(
              (tradingAccount: TradingAccountDto) => tradingAccount
            ) || []

          setSelectedTradingAccounts(value)
          if (response?.items) {
            setTradingAccounts(
              response.items.map((tradingAccount: TradingAccountDto) => {
                if (value.length <= 0) {
                  return { ...tradingAccount, selected: false }
                }

                const match = value?.find((x: TradingAccountDto) => x.id === tradingAccount.id)
                const isSelected = !!match

                return { ...tradingAccount, selected: isSelected }
              })
            )
          }

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<TradingAccountDto[]>>
  ) => {
    setLoading(true)

    const newGridState = createGridState(getPaginationInitialState(), gridFilterState)
    const response = await callback(newGridState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      setGridFilterState({
        ...newGridState,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skip: gridState.skip,
        total,
      })
    }

    setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor): string | null => {
      return (
        buildQuery(normalizeTradingAccountsSearchQuery(filter ?? gridFilterState.filter!)) ?? null
      )
    },
    [gridFilterState.filter]
  )

  const filterFetch = useCallback(
    async (params?: {
      query?: string | null
      filter?: CompositeFilterDescriptor
    }): Promise<void> => {
      const searchQuery =
        params?.query !== undefined ? params.query : getQuery(gridFilterState.filter)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await tradingAccountsApiClient.getTradingAccounts({
        search: searchQuery,
        pageIndex,
        pageSize,
      })
      const total = response.totalResults

      if (response?.items) {
        setTradingAccounts(
          response.items.map((dataItem: TradingAccountDto) => ({ ...dataItem, selected: false }))
        )
      }
      setGridFilterState({
        ...gridFilterState,
        filter: params?.filter ?? gridFilterState.filter,
        take,
        skip,
        total,
        pageIndex,
      })
    },
    [gridFilterState, gridState.pageSize, gridState.take, getQuery] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const selectTradingAccounts = (tradingAccountItems: TradingAccountDto[]) =>
    tradingAccountItems.map((tradingAccount: TradingAccountDto) => {
      const match = selectedTradingAccounts.find((x) => x.id === tradingAccount.id)
      const isSelected = !!match
      return { ...tradingAccount, selected: isSelected }
    })

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip: event.page.skip,
      recordsPerPage: take,
    })

    const searchQuery = getQuery(gridFilterState.filter)
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
    })

    const response = await tradingAccountsApiClient.getTradingAccounts({
      search: searchQuery,
      pageIndex,
      pageSize,
    })
    const total = response.totalResults

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
      total,
    })
    if (response?.items) {
      setTradingAccounts(selectTradingAccounts(response.items))
    }
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setTradingAccounts(
      tradingAccounts.map((tradingAccount) => ({ ...tradingAccount, selected: isChecked }))
    )

    const tradingAccountsSelected = isChecked
      ? uniqBy([...tradingAccounts, ...selectedTradingAccounts], 'id')
      : selectedTradingAccounts.filter((selectedTradingAccount) => {
          const hasTradingAccounts = tradingAccounts.find((x) => x.id === selectedTradingAccount.id)

          return !hasTradingAccounts
        })

    setSelectedTradingAccounts(tradingAccountsSelected)
  }

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem.selected) {
      const tradingAccount = tradingAccounts.find((x) => x.id === event.dataItem.id)
      if (tradingAccount) {
        setSelectedTradingAccounts([...selectedTradingAccounts, tradingAccount])
      }
    } else {
      setSelectedTradingAccounts(selectedTradingAccounts.filter((x) => x.id !== event.dataItem.id))
    }

    setTradingAccounts(
      tradingAccounts.map((item) => {
        if (item.id === event.dataItem.id) {
          return { ...item, selected: !event.dataItem.selected }
        }
        return item
      })
    )
  }

  const handleSubmitTradingAccounts = () => {
    props.onSelect({
      filterId: props.filterId,
      value: uniqBy(selectedTradingAccounts, 'id'),
    })
    props.onClose()
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <div className='trading-account-table' style={{ margin: 16 }}>
      <SelectFiltersFacade
        gridState={gridFilterState}
        setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
        filterFetch={filterFetch}
        filterType={FilterType.TRADING_ACCOUNT}
        filterFields={getTradingAccountFilterFields()}
        containerClassName='trading-account-table__filter'
        getQuery={getQuery}
        showFilterWhenInactive
      />
      <Grid
        {...gridFilterState}
        resizable
        data={tradingAccounts}
        selectedField='selected'
        total={gridFilterState.total}
        take={gridFilterState.take}
        skip={gridFilterState.skip}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onPageChange={handleGridPageChange}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        <Column
          field='selected'
          width='50px'
          headerSelectionValue={tradingAccounts.findIndex((dataItem) => !dataItem.selected) === -1}
        />
        <Column field='name' title='Name' />
        <Column field='wallet.name' title='Wallet' />
        <Column field='client.name' title='Client' />
        <Column field='introducingBroker.name' title='Introducing Broker' />
        <Column field='accountType.name' title='Account Type' />
        <Column field='currency.id' title='Currency' />
      </Grid>
      <FlexBox style={{ margin: 32 }} alignItems='center' justifyContent='center'>
        <Button style={{ cursor: 'pointer' }} onClick={handleSubmitTradingAccounts}>
          Save
        </Button>
      </FlexBox>
    </div>
  )
}

const findFilterById = (
  filterId?: string,
  filters?: (FilterDescriptor | CompositeFilterDescriptor)[]
) => {
  return (
    (filters?.find((filter: FilterDescriptor | CompositeFilterDescriptor) => {
      if (instanceOfFilterDescriptor(filter)) {
        return filter.id === filterId
      }
      return false
    }) as FilterDescriptor) ?? []
  )
}

export default SelectTradingAccountTable
